import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CollabDocStorage } from '@common/collab/enums/collab-doc-storage.enum';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabCommitMergeResult, CollabCommitResult } from '@common/collab/models/collab-commit-result.model';
import { CollabDashboardData } from '@common/collab/models/collab-dashboard-data.model';
import { CollabDiscardResult } from '@common/collab/models/collab-discard-result.model';
import { CollabDocCommitRequestBody } from '@common/collab/models/collab-doc-commit-request-body.model';
import { CollabDocContentRequestBody } from '@common/collab/models/collab-doc-content-request-body.model';
import { CollabDocCreateRequestBody } from '@common/collab/models/collab-doc-create-request-body.model';
import { CollabDocDeleteRequestBody } from '@common/collab/models/collab-doc-delete-request-body.model';
import { CollabDocDiscardChangesRequestBody } from '@common/collab/models/collab-doc-discard-changes-request-body.model';
import { MergeCaseFixture } from '@common/collab/merge-failure-fixture';
import { CollabDocErrorItem } from '@common/collab/models/collab-doc-error-item.model';
import { CollabDocMergeFailureItem } from '@common/collab/models/collab-doc-merge-failure-item.model';
import { CollabDocMongoItem } from '@common/collab/models/collab-doc-mongo-item.model';
import { CollabDocPullOrFetchRequestBody } from '@common/collab/models/collab-doc-pull-or-fetch-request-body.model';
import { CollabDocPullRequestBody } from '@common/collab/models/collab-doc-pull-request-body.model';
import { CollabDocRedisItem } from '@common/collab/models/collab-doc-redis-item.model';
import { CollabDocRedisJSON } from '@common/collab/models/collab-doc-redis-json.model';
import { CollabDocRenameRequestBody } from '@common/collab/models/collab-doc-rename-request-body.model';
import { CollabDocRestoreVersionRequestBody } from '@common/collab/models/collab-doc-restore-version-request-body.model';
import { CollabDocStepChunkRequestBody } from '@common/collab/models/collab-doc-step-chunk-request-body.model';
import { CollabDocStepChunk } from '@common/collab/models/collab-doc-step-chunk.model';
import { CollabMetaDataMongoItem } from '@common/collab/models/collab-meta-data-mongo-item.model';
import { PerfChildBreakdown, PerfOperationItem, PerfSeries, PerfSummary } from '@common/collab/models/perf-snapshot.model';
import { ProjectFileParameters, ReviewFileParameters } from '@common/collab/types/doc-parameters.type';
import { DomCompareResult } from '@common/html/types/html-compare.type';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { StepJSON } from '@common/prosemirror/transform/json';
import { SyncFailure, SyncFailureSource } from '@common/collab/models/sync-failure.model';
import { WorkspaceFileUserStatus } from '@common/workspace/enums/workspace-file-user-status.enum';
import { WorkspaceFetchResult } from '@common/workspace/models/workspace-fetch-result.model';
import { WorkspaceFile } from '@common/workspace/models/workspace-file.model';
import { WorkspacePullRequestBody } from '@common/workspace/models/workspace-pull-request-body.model';
import { WorkspacePullStateGridItem } from '@common/workspace/models/workspace-pull-state-grid-item.model';
import { WorkspacePullStateResponse } from '@common/workspace/models/workspace-pull-state-response.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { ProjectFile } from '@portal-core/project-files/models/project-file.model';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Base64 } from 'js-base64';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CollabApiService {
  constructor(private http: HttpClient, private apiService: ApiService) { }

  @SharePendingRequest()
  getCollabHealth$(): Observable<any> {
    return this.http.get<any>(`${this.apiService.collabApiUrl}/api/health`);
  }

  @SharePendingRequest()
  getDashboardData$(): Observable<CollabDashboardData> {
    return this.http.get<CollabDashboardData>(`${this.apiService.collabApiUrl}/api/dashboard`);
  }

  @SharePendingRequest()
  getCollabDocMongoItemsPage$(filter: PageFilter): Observable<Page<CollabDocMongoItem>> {
    return this.http.post<Page<CollabDocMongoItem>>(`${this.apiService.collabApiUrl}/api/docs/mongo`, filter);
  }

  @SharePendingRequest()
  getCollabDocRedisItemsPage$(filter: PageFilter): Observable<Page<CollabDocRedisItem>> {
    return this.http.post<Page<CollabDocRedisItem>>(`${this.apiService.collabApiUrl}/api/docs/redis`, filter);
  }

  @SharePendingRequest()
  getCollabDocErrorItemsPage$(filter: PageFilter): Observable<Page<CollabDocErrorItem>> {
    return this.http.post<Page<CollabDocErrorItem>>(`${this.apiService.collabApiUrl}/api/docs/errors`, filter);
  }

  @SharePendingRequest()
  getSyncFailuresPage$(filter: PageFilter, source: SyncFailureSource): Observable<Page<SyncFailure>> {
    // The three failed-* grids share one deduped Mongo collection but keep their established URLs.
    const url = source === 'doc' ? '/api/docs/failed-syncs' : source === 'workspace' ? '/api/workspaces/failed-syncs' : '/api/workspaces/failed-pulls';
    return this.http.post<Page<SyncFailure>>(`${this.apiService.collabApiUrl}${url}`, filter);
  }

  resolveSyncFailure$(id: string, resolved: boolean): Observable<void> {
    return this.http.post<void>(`${this.apiService.collabApiUrl}/api/sync-failures/${id}/resolve`, { resolved });
  }

  /**
   * Fetches a page of aggregated perf-operation rows for a service (or all services when `service` is
   * undefined) rolled up across the trailing `rangeMinutes` window.
   */
  @SharePendingRequest()
  getPerfOperationsPage$(filter: PageFilter, service: string | undefined, rangeMinutes: number): Observable<Page<PerfOperationItem>> {
    return this.http.post<Page<PerfOperationItem>>(`${this.apiService.collabApiUrl}/api/perf-snapshots/operations`, { ...filter, service, rangeMinutes });
  }

  /** Fetches the headline perf scorecards for a service (or all services) over the trailing `rangeMinutes` window. */
  @SharePendingRequest()
  getPerfSummary$(service: string | undefined, rangeMinutes: number): Observable<PerfSummary> {
    return this.http.post<PerfSummary>(`${this.apiService.collabApiUrl}/api/perf-snapshots/summary`, { service, rangeMinutes });
  }

  /** Fetches a single operation's time series (trend + throughput) over the trailing `rangeMinutes` window. */
  @SharePendingRequest()
  getPerfSeries$(service: string | undefined, name: string, rangeMinutes: number): Observable<PerfSeries> {
    return this.http.post<PerfSeries>(`${this.apiService.collabApiUrl}/api/perf-snapshots/series`, { service, name, rangeMinutes });
  }

  /** Fetches a single operation's one-level time breakdown (direct children + self) over the trailing `rangeMinutes` window. */
  @SharePendingRequest()
  getPerfOperationChildren$(service: string | undefined, name: string, rangeMinutes: number): Observable<PerfChildBreakdown> {
    return this.http.post<PerfChildBreakdown>(`${this.apiService.collabApiUrl}/api/perf-snapshots/children`, { service, name, rangeMinutes });
  }

  @SharePendingRequest()
  getMergeFailurePage$(filter: PageFilter): Observable<Page<CollabDocMergeFailureItem>> {
    return this.http.post<Page<CollabDocMergeFailureItem>>(`${this.apiService.collabApiUrl}/api/docs/merge-failures`, filter);
  }

  @SharePendingRequest()
  getMergeFailureFixture$(docId: string, incomingCommitId: string, scrubbed: boolean = true): Observable<MergeCaseFixture> {
    return this.http.get<MergeCaseFixture>(`${this.apiService.collabApiUrl}/api/docs/merge-failures/${docId}/${incomingCommitId}/fixture`, {
      params: { scrubbed }
    });
  }

  @SharePendingRequest()
  removeMergeFailure$(docId: string, incomingCommitId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiService.collabApiUrl}/api/docs/merge-failures/${docId}/${incomingCommitId}`);
  }

  @SharePendingRequest()
  getCollabPullsPage$(filter: PageFilter): Observable<Page<WorkspacePullStateGridItem>> {
    return this.http.post<Page<WorkspacePullStateGridItem>>(`${this.apiService.collabApiUrl}/api/workspaces/pulls`, filter);
  }

  getMongoDocCode$(docId: string): Observable<string> {
    return this.http.get(`${this.apiService.collabApiUrl}/api/docs/${docId}/code/mongo`, {
      responseType: 'text'
    }).pipe(
      map(code => {
        return Base64.decode(code);
      })
    );
  }

  @SharePendingRequest()
  getRedisDocCode$(docId: string, schemaName: string): Observable<string> {
    return this.http.get(`${this.apiService.collabApiUrl}/api/docs/${docId}/code/redis/${schemaName}`, {
      responseType: 'text'
    }).pipe(
      map(code => {
        return Base64.decode(code);
      })
    );
  }

  @SharePendingRequest()
  getRedisDocByDocId$(docId: string, schemaName: string): Observable<CollabDocRedisItem> {
    return this.http.get<CollabDocRedisItem>(`${this.apiService.collabApiUrl}/api/docs/${docId}/redis/${schemaName}`);
  }

  @SharePendingRequest()
  getMongoDoc$(docId: string): Observable<CollabDocMongoItem> {
    return this.http.get<CollabDocMongoItem>(`${this.apiService.collabApiUrl}/api/docs/${docId}/mongo`);
  }

  @SharePendingRequest()
  getMetaDataPage$(filter: PageFilter): Observable<Page<CollabMetaDataMongoItem>> {
    return this.http.post<Page<CollabMetaDataMongoItem>>(`${this.apiService.collabApiUrl}/api/metadata`, filter);
  }

  @SharePendingRequest()
  removeDocFromStorage$(docId: string, storage: CollabDocStorage, schemaName: string): Observable<void> {
    return this.http.delete<void>(`${this.apiService.collabApiUrl}/api/docs/${docId}/${storage}/${schemaName}`);
  }

  @SharePendingRequest()
  roundtrip$(code: string, schemaName: string): Observable<MadCloudResult<DomCompareResult>> {
    return this.http.post<MadCloudResult<DomCompareResult>>(`${this.apiService.collabApiUrl}/api/docs/roundtrip/${schemaName}`, {
      code: Base64.encode(code)
    });
  }

  @SharePendingRequest()
  getCollabDoc$(fileType: CollabFileType, fileParams: ProjectFileParameters | ReviewFileParameters): Observable<CollabDocRedisJSON> {
    return this.http.post<CollabDocRedisJSON>(`${this.apiService.collabAuthorityUrl}/api/docs`, { fileType, ...fileParams });
  }

  @SharePendingRequest()
  getWorkspaceFile$(projectId: number, branchName: string, filePath: string): Observable<WorkspaceFile> {
    return this.http.post<WorkspaceFile>(`${this.apiService.collabApiUrl}/api/workspaces/workspaceFile`, { projectId, branchName, filePath });
  }

  bulkUpdateWorkspaceFileStatus$(projectId: number, docIds: string[], schemaNames: string[], userId: string, status: WorkspaceFileUserStatus, docVersions?: number[]): Observable<any> {
    return this.http.post<any>(`${this.apiService.collabAuthorityUrl}/api/workspaces/updateWorkspaceFileUserStatus`, {
      docIds,
      docVersions,
      projectId,
      schemaNames,
      status,
      userId
    });
  }

  @SharePendingRequest()
  getWorkspaceFilesPage$(filter: PageFilter, projectId: number, branchName: string): Observable<any> {
    return this.http.post<any>(`${this.apiService.collabApiUrl}/api/workspaces/${projectId}/${encodeURIComponent(branchName)}/workspaceFilesPage`, { filter });
  }

  getCollabDocByDocId$(docId: string, schemaName: string): Observable<CollabDocRedisJSON> {
    return this.http.get<CollabDocRedisJSON>(`${this.apiService.collabAuthorityUrl}/api/docs/doc/${docId}/${schemaName}`);
  }

  @SharePendingRequest()
  getCollabDocStepChunk$(body: CollabDocStepChunkRequestBody): Observable<CollabDocStepChunk> {
    return this.http.post<CollabDocStepChunk>(`${this.apiService.collabAuthorityUrl}/api/docs/stepChunk`, body);
  }

  @SharePendingRequest()
  getCollabDocContent$(body: CollabDocContentRequestBody): Observable<string> {
    return this.http.post<string>(`${this.apiService.collabAuthorityUrl}/api/docs/content`, body, {
      responseType: 'text' as 'json'
    }).pipe(
      map(content => {
        return content ? Base64.decode(content) : content;
      })
    );
  }

  getDocContentPath(projectId: number, branchName: string, filePath: string): string {
    return `${this.apiService.collabAuthorityUrl}/content/${projectId}/${encodeURIComponent(branchName)}/${filePath}`;
  }

  commitCollabDocs$(body: CollabDocCommitRequestBody): Observable<CollabCommitResult> {
    return this.http.post<CollabCommitResult>(`${this.apiService.collabAuthorityUrl}/api/docs/commit`, body);
  }

  createCollabDoc$(body: CollabDocCreateRequestBody): Observable<void> {
    return this.http.post<void>(`${this.apiService.collabAuthorityUrl}/api/docs/create`, body);
  }

  deleteCollabDoc$(body: CollabDocDeleteRequestBody): Observable<CollabCommitResult> {
    return this.http.post<CollabCommitResult>(`${this.apiService.collabAuthorityUrl}/api/docs/delete`, body);
  }

  discardChangesFromCollabDocs$(body: CollabDocDiscardChangesRequestBody): Observable<CollabDiscardResult[]> {
    return this.http.post<CollabDiscardResult[]>(`${this.apiService.collabAuthorityUrl}/api/docs/discardChanges`, body);
  }

  renameCollabDoc$(body: CollabDocRenameRequestBody): Observable<CollabCommitResult> {
    return this.http.post<CollabCommitResult>(`${this.apiService.collabAuthorityUrl}/api/docs/rename`, body);
  }

  pullCollabDoc$(body: CollabDocPullRequestBody): Observable<CollabCommitMergeResult> {
    return this.http.post<CollabCommitMergeResult>(`${this.apiService.collabAuthorityUrl}/api/docs/pull`, body);
  }

  pullOrFetchCollabDocs$(body: CollabDocPullOrFetchRequestBody): Observable<CollabCommitMergeResult> {
    return this.http.post<CollabCommitMergeResult>(`${this.apiService.collabAuthorityUrl}/api/docs/pullOrFetch`, body);
  }

  @SharePendingRequest()
  fetchWorkspace$(projectId: number, branchName: string): Observable<MadCloudResult<WorkspaceFetchResult>> {
    return this.http.post<MadCloudResult<WorkspaceFetchResult>>(`${this.apiService.collabAuthorityUrl}/api/workspaces/fetch`, { projectId, branchName });
  }

  getWorkspaceFileTree$(projectId: number, branchName: string, treePath: string, includeFiles: boolean, fileFilter: string, committedFilter: boolean): Observable<ProjectFile[]> {
    return this.http.post<any[]>(`${this.apiService.collabAuthorityUrl}/api/workspaces/fileTree`, {
      projectId,
      branchName,
      treePath,
      includeFiles,
      fileFilter,
      committedFilter
    });
  }

  /**
   * Kicks off a workspace pull. Always returns `MadCloudResult<WorkspacePullStateResponse>`:
   *  - 202 with a `running` `pullState` when the pull was queued on collab-pull. The terminal
   *    result will arrive via the `workspace.pull.completed` socket event; the HTTP body is
   *    informational only.
   *  - 200 with a terminal `completed` `pullState` (empty merge result) when the server-side
   *    gate check found nothing to pull and short-circuited. The caller drives terminal success
   *    off the response body — no socket event will fire for this case.
   *  - 409 with the in-flight `pullState` when another client (or another tab of the same user)
   *    already has a pull running for this workspace. Surfaces as an HTTP error to subscribers;
   *    the body still carries a usable `pullState` so the caller can bind to the existing pull
   *    instead of starting a competing one.
   */
  pullWorkspace$(body: WorkspacePullRequestBody): Observable<MadCloudResult<WorkspacePullStateResponse>> {
    return this.http.post<MadCloudResult<WorkspacePullStateResponse>>(`${this.apiService.collabAuthorityUrl}/api/workspaces/pull`, body);
  }

  /**
   * Fetches the current workspace pull state, or `null` if no pull has run recently.
   * Used by the client to recover progress when (re-)opening the dialog or after a socket reconnect.
   */
  @SharePendingRequest()
  getWorkspacePullState$(projectId: number, branchName: string): Observable<MadCloudResult<WorkspacePullStateResponse>> {
    const params = { projectId: projectId.toString(), branchName };
    return this.http.get<MadCloudResult<WorkspacePullStateResponse>>(`${this.apiService.collabAuthorityUrl}/api/workspaces/pull/state`, { params });
  }

  restoreCollabDocToVersion$(body: CollabDocRestoreVersionRequestBody): Observable<{ docId: string, invertedSteps: StepJSON[], steps: StepJSON[] }> {
    return this.http.post<{ docId: string, invertedSteps: StepJSON[], steps: StepJSON[] }>(`${this.apiService.collabAuthorityUrl}/api/docs/restore`, body);
  }
}
